import "./Accreditations.scss";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import { collection, doc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { pdf } from "@react-pdf/renderer";
import QRCode from "qrcode";
import { read as readWorkbook, utils as xlsxUtils } from "xlsx";
import ENV from "../../../env";
import { db, storage } from "../../../firebase";
import AccreditationPDF from "../../../components/pdf/AccreditationPDF";
import EmailCustomizationModal, {
  DEFAULT_TICKET_EMAIL_CUSTOMIZATION,
} from "../ticketing/ticketingCreateEdit/settingsModal/EmailCustomizationModal";
import { uploadMedia } from "../../../utility/uploadMedia";
import { analyzeAccreditationsCsv } from "../../../utility/aiApi";
import {
  createAccreditation,
  importAccreditations,
  revokeAccreditationQr,
  sendAccreditationEmail,
  updateAccreditation,
} from "../../../utility/accreditationApi";

const DEFAULT_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  type: "volunteer",
  status: "active",
  photoUrl: "",
  notes: "",
  accessRules: [
    {
      label: "Accès organisation",
      zone: "",
      eventId: "",
      startsAt: "",
      endsAt: "",
      maxScans: "",
      scanMode: "multiple",
      validityMode: "always",
    },
  ],
};

const createEmptyAccessRule = () => ({
  label: "",
  zone: "",
  eventId: "",
  startsAt: "",
  endsAt: "",
  maxScans: "",
  scanMode: "multiple",
  validityMode: "always",
});

const typeOptions = [
  "volunteer",
  "staff",
  "guest",
  "press",
  "provider",
  "artist",
  "security",
  "vip",
  "other",
];

const statusOptions = ["active", "suspended", "expired", "draft", "archived"];

const QR_PREFIX = "COMITTY_ACC:v1";
const CSV_SEPARATOR = ";";
const DEFAULT_ACCREDITATION_EMAIL_PRIMARY_COLOR = "#4200ff";
const DEFAULT_ACCREDITATION_EMAIL_SECONDARY_COLOR = "#ff02c7";

const getOrganizationDisplayName = (org) =>
  org?.organization || org?.orgName || org?.name || org?.displayName || "Comitty";

const ACCREDITATION_TEMPLATE_HEADERS = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "type",
  "status",
  "notes",
  "accessLabel",
  "zone",
  "eventId",
  "validFrom",
  "validUntil",
  "maxScans",
  "scanMode",
];

const ACCREDITATION_EXPORT_HEADERS = [
  "id",
  ...ACCREDITATION_TEMPLATE_HEADERS,
  "photoUrl",
  "scanCount",
  "emailStatus",
  "lastEmailSentAt",
  "lastScannedAt",
  "createdAt",
  "updatedAt",
];

const csvEscape = (value) => {
  const normalized = String(value ?? "").replace(/\r?\n/g, " ").trim();
  if (
    normalized.includes(CSV_SEPARATOR) ||
    normalized.includes('"') ||
    normalized.includes("\n")
  ) {
    return `"${normalized.replace(/"/g, '""')}"`;
  }
  return normalized;
};

const downloadCsv = (fileName, headers, rows) => {
  const content = [headers, ...rows]
    .map((row) => row.map(csvEscape).join(CSV_SEPARATOR))
    .join("\n");
  const blob = new Blob([`\uFEFF${content}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const sanitizeFilePart = (value) =>
  String(value || "organisation")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

const formatTimestampForCsv = (value) => {
  const timestamp = Number(value || 0);
  if (!Number.isFinite(timestamp) || timestamp <= 0) return "";
  return new Date(timestamp).toISOString();
};

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const parseDelimitedRows = (text) => {
  const firstLine = text.split(/\r?\n/).find((line) => line.trim()) || "";
  const delimiter = firstLine.includes("\t") ? "\t" : firstLine.includes(";") ? ";" : ",";
  const rows = [];
  let row = [];
  let value = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];
    if (char === '"') {
      if (inQuotes && next === '"') {
        value += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (!inQuotes && char === delimiter) {
      row.push(value);
      value = "";
      continue;
    }
    if (!inQuotes && (char === "\n" || char === "\r")) {
      if (char === "\r" && next === "\n") i += 1;
      row.push(value);
      if (row.some((cell) => String(cell || "").trim())) rows.push(row);
      row = [];
      value = "";
      continue;
    }
    value += char;
  }
  row.push(value);
  if (row.some((cell) => String(cell || "").trim())) rows.push(row);
  return rows;
};

const readRows = async (file) => {
  const buffer = await file.arrayBuffer();
  const name = file.name.toLowerCase();
  if (name.endsWith(".csv")) {
    const bytes = new Uint8Array(buffer);
    const encoding = bytes[0] === 0xff && bytes[1] === 0xfe ? "utf-16le" : "utf-8";
    return parseDelimitedRows(new TextDecoder(encoding).decode(bytes).replace(/^\uFEFF/, ""));
  }
  const workbook = readWorkbook(new Uint8Array(buffer), { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  return xlsxUtils.sheet_to_json(sheet, { header: 1 });
};

const getFullName = (item) => `${item.firstName || ""} ${item.lastName || ""}`.trim();

const toDatetimeLocalValue = (value) => {
  const timestamp = Number(value || 0);
  if (!Number.isFinite(timestamp) || timestamp <= 0) return "";
  const date = new Date(timestamp);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

function Accreditations({ org }) {
  const { t } = useTranslation("accreditations");
  const alert = useAlert();
  const fileInputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [photoFile, setPhotoFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [analyzingImport, setAnalyzingImport] = useState(false);
  const [events, setEvents] = useState([]);
  const [sendingId, setSendingId] = useState(null);
  const [showEmailCustomizationModal, setShowEmailCustomizationModal] = useState(false);
  const [accreditationEmailCustomization, setAccreditationEmailCustomization] = useState({
    ...DEFAULT_TICKET_EMAIL_CUSTOMIZATION,
    ...(org?.accreditationEmailCustomization || {}),
  });

  const venueId = org?.uid || org?.id;

  useEffect(() => {
    setAccreditationEmailCustomization({
      ...DEFAULT_TICKET_EMAIL_CUSTOMIZATION,
      ...(org?.accreditationEmailCustomization || {}),
    });
  }, [org?.accreditationEmailCustomization, org?.id]);

  const loadAccreditations = useCallback(async () => {
    if (!org?.id) return;
    setLoading(true);
    try {
      const snapshot = await getDocs(query(collection(db, "Accreditations"), where("orgId", "==", org.id)));
      setItems(
        snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0)),
      );
    } catch (error) {
      alert.show(error.message || t("load_error"), { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [alert, org?.id, t]);

  const loadEvents = useCallback(async () => {
    if (!org?.id) return;
    try {
      const snapshot = await getDocs(query(collection(db, "Events"), where("orgId", "==", org.id)));
      setEvents(
        snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => Number(a.startDate || a.createdAt || 0) - Number(b.startDate || b.createdAt || 0)),
      );
    } catch (error) {
      console.error("Accreditations events load failed", error);
      setEvents([]);
    }
  }, [org?.id]);

  useEffect(() => {
    loadAccreditations();
    loadEvents();
  }, [loadAccreditations, loadEvents]);

  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return {
      active: items.filter((item) => item.status === "active").length,
      suspended: items.filter((item) => item.status === "suspended").length,
      expired: items.filter((item) => item.status === "expired").length,
      scansToday: items.filter((item) => Number(item.lastScannedAt || 0) >= today.getTime()).length,
    };
  }, [items]);

  const filteredItems = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesSearch =
        !needle ||
        getFullName(item).toLowerCase().includes(needle) ||
        String(item.email || "").toLowerCase().includes(needle) ||
        String(item.phone || "").toLowerCase().includes(needle);
      const matchesStatus = statusFilter === "all" || item.status === statusFilter;
      const matchesType = typeFilter === "all" || item.type === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [items, search, statusFilter, typeFilter]);

  const openCreateModal = () => {
    setEditing(null);
    setForm(DEFAULT_FORM);
    setPhotoFile(null);
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditing(item);
    setForm({
      ...DEFAULT_FORM,
      ...item,
      accessRules: item.accessRules?.length
        ? item.accessRules.map((rule) => ({
            ...rule,
            startsAt: toDatetimeLocalValue(rule.startsAt),
            endsAt: toDatetimeLocalValue(rule.endsAt),
            maxScans: rule.maxScans || "",
            validityMode: rule.startsAt || rule.endsAt ? "custom" : "always",
          }))
        : DEFAULT_FORM.accessRules,
    });
    setPhotoFile(null);
    setModalOpen(true);
  };

  const uploadPhotoIfNeeded = async () => {
    if (!photoFile || !org?.id) return form.photoUrl || "";
    const path = `accreditations/${org.id}/photos/${Date.now()}-${photoFile.name}`;
    const photoRef = ref(storage, path);
    await uploadBytesResumable(photoRef, photoFile, { contentType: photoFile.type });
    return getDownloadURL(photoRef);
  };

  const submitForm = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const photoUrl = await uploadPhotoIfNeeded();
      const payload = {
        ...form,
        photoUrl,
        orgId: org.id,
        venueId,
        accessRules: form.accessRules
          .filter((rule) => rule.label || rule.zone || rule.eventId)
          .map((rule) => ({
            ...rule,
            startsAt: rule.validityMode === "custom" ? rule.startsAt || "" : "",
            endsAt: rule.validityMode === "custom" ? rule.endsAt || "" : "",
          })),
      };
      if (editing?.id) {
        await updateAccreditation({ ...payload, accreditationId: editing.id });
      } else {
        await createAccreditation(payload);
      }
      setModalOpen(false);
      await loadAccreditations();
      alert.show(t("save_success"), { type: "success" });
    } catch (error) {
      alert.show(error.message || t("save_error"), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const getQrPayload = (item) => `${QR_PREFIX}:${item.id}:${item.qrToken}`;

  const buildPdfBlob = async (item) => {
    const qrCode = await QRCode.toDataURL(getQrPayload(item), { scale: 8, margin: 1 });
    return pdf(
      <AccreditationPDF
        accreditation={item}
        organization={org}
        qrCode={qrCode}
        emailCustomization={accreditationEmailCustomization}
      />,
    ).toBlob();
  };

  const downloadPdf = async (item) => {
    const blob = await buildPdfBlob(item);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `accreditation-${getFullName(item).replace(/\s+/g, "-").toLowerCase()}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const sendEmail = async (item) => {
    setSendingId(item.id);
    try {
      const blob = await buildPdfBlob(item);
      const content = await blobToBase64(blob);
      const accreditationQrUrl = await QRCode.toDataURL(getQrPayload(item), {
        scale: 7,
        margin: 1,
        width: 260,
        color: {
          dark: "#0f172a",
          light: "#ffffff",
        },
      });
      const customization = accreditationEmailCustomization || {};
      const customEnabled = Boolean(customization.enabled && ENV.emailJsAccreditationTemplateId);
      const organizationName = getOrganizationDisplayName(org);
      await sendAccreditationEmail({
        accreditationId: item.id,
        content,
        accreditationQrUrl,
        templateId: customEnabled ? ENV.emailJsAccreditationTemplateId : undefined,
        organizationName,
        subject: customEnabled
          ? customization.subject || t("email_subject", { organization: organizationName })
          : t("email_subject", { organization: organizationName }),
        message: customEnabled ? customization.message || t("email_message") : t("email_message"),
        emailCustomization: customEnabled
          ? {
              ...customization,
              title: customization.title || t("accreditation_email_default_title"),
              headerImageUrl: customization.headerImageUrl || "",
              primaryColor: customization.primaryColor || DEFAULT_ACCREDITATION_EMAIL_PRIMARY_COLOR,
              secondaryColor: customization.secondaryColor || DEFAULT_ACCREDITATION_EMAIL_SECONDARY_COLOR,
              footerText: customization.footerText || "",
            }
          : null,
      });
      const sentAt = Date.now();
      setItems((current) =>
        current.map((accreditation) =>
          accreditation.id === item.id
            ? {
                ...accreditation,
                emailStatus: "sent",
                lastEmailSentAt: sentAt,
                updatedAt: sentAt,
              }
            : accreditation,
        ),
      );
      alert.show(t("email_sent"), { type: "success" });
    } catch (error) {
      alert.show(error.message || t("email_error"), { type: "error" });
    } finally {
      setSendingId(null);
    }
  };

  const revokeQr = async (item) => {
    if (!window.confirm(t("confirm_revoke"))) return;
    setSaving(true);
    try {
      await revokeAccreditationQr({ accreditationId: item.id });
      await loadAccreditations();
      alert.show(t("qr_revoked"), { type: "success" });
    } catch (error) {
      alert.show(error.message || t("save_error"), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (item, status) => {
    setSaving(true);
    try {
      await updateAccreditation({ ...item, orgId: org.id, accreditationId: item.id, status });
      await loadAccreditations();
    } catch (error) {
      alert.show(error.message || t("save_error"), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      alert.show(t("csv_only_error"), { type: "error" });
      event.target.value = "";
      return;
    }
    setAnalyzingImport(true);
    try {
      const rows = await readRows(file);
      const result = await analyzeAccreditationsCsv({ orgId: org.id, rows });
      setImportPreview({ fileName: file.name, candidates: result.candidates || [] });
    } catch (error) {
      alert.show(error.message || t("import_error"), { type: "error" });
    } finally {
      setAnalyzingImport(false);
      event.target.value = "";
    }
  };

  const confirmImport = async () => {
    const selectedCandidates = (importPreview?.candidates || []).filter((candidate) => candidate.selected);
    if (!selectedCandidates.length) return;
    setSaving(true);
    try {
      const accreditations = selectedCandidates.map((candidate) => ({
        firstName: candidate.firstName,
        lastName: candidate.lastName,
        email: candidate.email,
        phone: candidate.phone,
        type: candidate.type,
        status: candidate.status,
        notes: candidate.notes,
        accessRules:
          candidate.accessLabel || candidate.zone || candidate.eventId
            ? [
                {
                  label: candidate.accessLabel,
                  zone: candidate.zone,
                  eventId: candidate.eventId,
                  startsAt: candidate.validFrom,
                  endsAt: candidate.validUntil,
                  maxScans: candidate.maxScans,
                  scanMode: candidate.scanMode,
                },
              ]
            : [],
      }));
      const result = await importAccreditations({
        orgId: org.id,
        accreditations,
      });
      setImportPreview(null);
      await loadAccreditations();
      alert.show(t("import_success", result), { type: "success" });
    } catch (error) {
      alert.show(error.message || t("import_error"), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const exportAccreditationsCsv = () => {
    const rows = items.map((item) => {
      const accessRules = Array.isArray(item.accessRules) ? item.accessRules : [];
      const joinRules = (selector) =>
        accessRules
          .map(selector)
          .filter((value) => value !== null && value !== undefined && value !== "")
          .join(" | ");
      return [
        item.id || "",
        item.firstName || "",
        item.lastName || "",
        item.email || "",
        item.phone || "",
        item.type || "",
        item.status || "",
        item.notes || "",
        joinRules((rule) => rule.label),
        joinRules((rule) => rule.zone),
        joinRules((rule) => rule.eventId),
        joinRules((rule) => formatTimestampForCsv(rule.startsAt)),
        joinRules((rule) => formatTimestampForCsv(rule.endsAt)),
        joinRules((rule) => rule.maxScans),
        joinRules((rule) => rule.scanMode),
        item.photoUrl || "",
        Number(item.scanCount || 0),
        item.emailStatus || "",
        formatTimestampForCsv(item.lastEmailSentAt),
        formatTimestampForCsv(item.lastScannedAt),
        formatTimestampForCsv(item.createdAt),
        formatTimestampForCsv(item.updatedAt),
      ];
    });
    downloadCsv(
      `accreditations-${sanitizeFilePart(getOrganizationDisplayName(org) || org?.id)}.csv`,
      ACCREDITATION_EXPORT_HEADERS,
      rows,
    );
  };

  const saveAccreditationEmailCustomization = async (customization) => {
    if (!org?.id) return;
    setSaving(true);
    try {
      let nextCustomization = { ...customization };
      if (
        nextCustomization.headerImageUrl &&
        nextCustomization.headerImageUrl !== accreditationEmailCustomization?.headerImageUrl
      ) {
        const url = await uploadMedia(
          nextCustomization.headerImageUrl,
          `accreditations/email-images/${org.id}.jpg`,
          false,
          {
            compressImage: {
              maxWidth: 1200,
              maxHeight: 520,
              maxBytes: 140 * 1024,
              mimeType: "image/jpeg",
              initialQuality: 0.82,
            },
          },
        );
        nextCustomization = { ...nextCustomization, headerImageUrl: url };
      }

      await updateDoc(doc(db, "Organizations", org.id), {
        accreditationEmailCustomization: nextCustomization,
      });
      setAccreditationEmailCustomization(nextCustomization);
      setShowEmailCustomizationModal(false);
      alert.show(t("email_customization_saved"), { type: "success" });
    } catch (error) {
      alert.show(error.message || t("save_error"), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const setAccessRule = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      accessRules: prev.accessRules.map((rule, ruleIndex) =>
        ruleIndex === index ? { ...rule, ...patch } : rule,
      ),
    }));
  };

  const updateImportCandidate = (index, patch) => {
    setImportPreview((current) => ({
      ...current,
      candidates: (current?.candidates || []).map((candidate, candidateIndex) =>
        candidateIndex === index ? { ...candidate, ...patch } : candidate,
      ),
    }));
  };

  const removeImportCandidate = (index) => {
    setImportPreview((current) => ({
      ...current,
      candidates: (current?.candidates || []).filter((_, candidateIndex) => candidateIndex !== index),
    }));
  };

  return (
    <div className="accreditationsPage">
      <div className="accreditationsHero">
        <div>
          <p className="accreditationsEyebrow">{t("eyebrow")}</p>
          <h1>{t("title")}</h1>
          <p>{t("subtitle")}</p>
        </div>
        <div className="accreditationsHeroActions">
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={handleImportFile} hidden />
          <button type="button" className="secondaryAction" onClick={exportAccreditationsCsv} disabled={!items.length}>
            {t("export_accreditations_csv")}
          </button>
          <button
            type="button"
            className={`secondaryAction ${analyzingImport ? "loadingAction" : ""}`}
            onClick={() => fileInputRef.current?.click()}
            disabled={analyzingImport}
          >
            {analyzingImport ? t("analyzing_csv") : t("import_ai_csv")}
          </button>
          <button type="button" className="primaryAction" onClick={openCreateModal}>
            + {t("new_accreditation")}
          </button>
        </div>
      </div>

      <section className="accreditationEmailCard">
        <div>
          <span>{t("ticketEmailCustomizationEyebrow")}</span>
          <h2>{t("ticketEmailCustomizationTitle")}</h2>
          <p>
            {accreditationEmailCustomization?.enabled
              ? t("ticketEmailCustomizationEnabled")
              : t("ticketEmailCustomizationDisabled")}
          </p>
        </div>
        <button
          type="button"
          className="secondaryAction"
          onClick={() => setShowEmailCustomizationModal(true)}
        >
          {t("customize_email")}
        </button>
      </section>

      <div className="accreditationStats">
        {["active", "suspended", "expired", "scansToday"].map((key) => (
          <article className={`statCard ${key}`} key={key}>
            <span>{t(`stats.${key}`)}</span>
            <strong>{stats[key]}</strong>
          </article>
        ))}
      </div>

      <div className="accreditationFilters">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">{t("all_statuses")}</option>
          {statusOptions.map((status) => (
            <option value={status} key={status}>
              {t(`status.${status}`)}
            </option>
          ))}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">{t("all_types")}</option>
          {typeOptions.map((type) => (
            <option value={type} key={type}>
              {t(`types.${type}`)}
            </option>
          ))}
        </select>
      </div>

      <section className="accreditationList">
        {loading ? (
          [...Array(6)].map((_, index) => <div className="accreditationSkeleton" key={index} />)
        ) : filteredItems.length ? (
          filteredItems.map((item) => (
            <article className="accreditationRow" key={item.id}>
              <div className="personCell">
                <div className="avatar">{item.photoUrl ? <img src={item.photoUrl} alt="" /> : getFullName(item).slice(0, 1)}</div>
                <div>
                  <strong>{getFullName(item)}</strong>
                  <span>{item.email}</span>
                </div>
              </div>
              <span className={`typeBadge ${item.type}`}>{t(`types.${item.type || "other"}`)}</span>
              <span className={`statusBadge ${item.status}`}>{t(`status.${item.status || "draft"}`)}</span>
              <div className="scanCell">
                <strong>{Number(item.scanCount || 0)}</strong>
                <span>{t("scans")}</span>
              </div>
              <div className="rowActions">
                <button type="button" onClick={() => openEditModal(item)}>{t("edit")}</button>
                <button
                  type="button"
                  className={sendingId === item.id ? "loadingAction" : ""}
                  onClick={() => sendEmail(item)}
                  disabled={sendingId === item.id}
                >
                  {sendingId === item.id ? t("sending") : t("send")}
                </button>
                <button type="button" onClick={() => downloadPdf(item)}>{t("pdf")}</button>
                {item.status === "active" ? (
                  <button type="button" className="danger" onClick={() => updateStatus(item, "suspended")}>{t("suspend")}</button>
                ) : (
                  <button type="button" onClick={() => updateStatus(item, "active")}>{t("activate")}</button>
                )}
              </div>
            </article>
          ))
        ) : (
          <div className="accreditationEmpty">
            <h2>{t("empty_title")}</h2>
            <p>{t("empty_text")}</p>
            <button type="button" className="primaryAction" onClick={openCreateModal}>{t("new_accreditation")}</button>
          </div>
        )}
      </section>

      {modalOpen && (
        <div className="accreditationModalOverlay">
          <form className="accreditationModal" onSubmit={submitForm}>
            <header>
              <div>
                <p className="accreditationsEyebrow">{editing ? t("edit") : t("create")}</p>
                <h2>{editing ? getFullName(editing) : t("new_accreditation")}</h2>
              </div>
              <button type="button" onClick={() => setModalOpen(false)}>×</button>
            </header>
            <div className="modalGrid">
              <section className="modalFields">
                <div className="fieldGrid two">
                  <label>{t("firstName")}<input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></label>
                  <label>{t("lastName")}<input required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></label>
                </div>
                <div className="fieldGrid two">
                  <label>{t("email")}<input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
                  <label>{t("phone")}<input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
                </div>
                <div className="fieldGrid two">
                  <label>{t("type")}<select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>{typeOptions.map((type) => <option value={type} key={type}>{t(`types.${type}`)}</option>)}</select></label>
                  <label>{t("status_label")}<select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{statusOptions.map((status) => <option value={status} key={status}>{t(`status.${status}`)}</option>)}</select></label>
                </div>
                <div className="photoField">
                  <span className="fieldLabel">{t("photo")}</span>
                  <label className="photoUploadControl">
                    <input type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} />
                    <span className="photoUploadButton">{t("choose_photo")}</span>
                    <span className="photoUploadName">
                      {photoFile?.name || (form.photoUrl ? t("photo_existing") : t("photo_empty"))}
                    </span>
                  </label>
                </div>
                <div className="accessRulesHeader">
                  <strong>{t("access_rules")}</strong>
                  <button type="button" onClick={() => setForm((prev) => ({ ...prev, accessRules: [...prev.accessRules, createEmptyAccessRule()] }))}>+ {t("add_access")}</button>
                </div>
                {form.accessRules.map((rule, index) => (
                  <div className="accessRuleCard" key={index}>
                    <div className="accessRuleMain">
                      <label>{t("access_label")}<input value={rule.label || ""} onChange={(e) => setAccessRule(index, { label: e.target.value })} /></label>
                      <label>{t("zone")}<input value={rule.zone || ""} onChange={(e) => setAccessRule(index, { zone: e.target.value })} /></label>
                      <label>{t("event_scope")}<select value={rule.eventId || ""} onChange={(e) => setAccessRule(index, { eventId: e.target.value })}>
                        <option value="">{t("all_events")}</option>
                        {events.map((event) => (
                          <option value={event.id} key={event.id}>
                            {event.name || event.title || event.id}
                          </option>
                        ))}
                      </select></label>
                      <button
                        type="button"
                        className="removeAccessButton"
                        aria-label={t("remove")}
                        onClick={() => setForm((prev) => ({ ...prev, accessRules: prev.accessRules.filter((_, i) => i !== index) }))}
                      >
                        ×
                      </button>
                    </div>
                    <div className="validityRow">
                      <div>
                        <span className="fieldLabel">{t("validity")}</span>
                        <div className="validityToggle">
                          <button
                            type="button"
                            className={(rule.validityMode || "always") === "always" ? "active" : ""}
                            onClick={() => setAccessRule(index, { validityMode: "always", startsAt: "", endsAt: "" })}
                          >
                            {t("validity_always")}
                          </button>
                          <button
                            type="button"
                            className={rule.validityMode === "custom" ? "active" : ""}
                            onClick={() => setAccessRule(index, { validityMode: "custom" })}
                          >
                            {t("validity_custom")}
                          </button>
                        </div>
                      </div>
                      <label>{t("max_scans_label")}<input type="number" min="0" value={rule.maxScans || ""} onChange={(e) => setAccessRule(index, { maxScans: e.target.value })} /></label>
                    </div>
                    {rule.validityMode === "custom" && (
                      <div className="validityDates">
                        <label>{t("valid_from")}<input type="datetime-local" value={rule.startsAt || ""} onChange={(e) => setAccessRule(index, { startsAt: e.target.value })} /></label>
                        <label>{t("valid_until")}<input type="datetime-local" value={rule.endsAt || ""} onChange={(e) => setAccessRule(index, { endsAt: e.target.value })} /></label>
                      </div>
                    )}
                  </div>
                ))}
                <label>{t("notes")}<textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
              </section>
              <aside className="badgePreview">
                <div className="badgeCard">
                  <span>{getOrganizationDisplayName(org)}</span>
                  <div className="badgeAvatar">{photoFile ? <img src={URL.createObjectURL(photoFile)} alt="" /> : form.photoUrl ? <img src={form.photoUrl} alt="" /> : getFullName(form).slice(0, 1) || "A"}</div>
                  <h3>{getFullName(form) || t("preview_name")}</h3>
                  <p>{t(`types.${form.type}`)}</p>
                  <div className="fakeQr">QR</div>
                  <small>{t("preview_help")}</small>
                </div>
                {editing?.id && <button type="button" className="secondaryAction revokeButton" onClick={() => revokeQr(editing)}>{t("revoke_qr")}</button>}
              </aside>
            </div>
            <footer>
              <button type="button" className="secondaryAction" onClick={() => setModalOpen(false)}>{t("cancel")}</button>
              <button type="submit" className="primaryAction" disabled={saving}>{saving ? t("saving") : t("save")}</button>
            </footer>
          </form>
        </div>
      )}

      {importPreview && (
        <div className="accreditationModalOverlay">
          <div className="accreditationModal importModal aiImportModal">
            <header>
              <div>
                <p className="accreditationsEyebrow">{t("import_ai_csv")}</p>
                <h2>{importPreview.fileName}</h2>
              </div>
              <button type="button" onClick={() => setImportPreview(null)}>×</button>
            </header>
            <p>
              {t("ai_import_preview", {
                count: importPreview.candidates.length,
                selected: importPreview.candidates.filter((candidate) => candidate.selected).length,
              })}
            </p>
            <div className="aiImportReview">
              {importPreview.candidates.map((candidate, index) => (
                <article className={`aiImportRow ${candidate.selected ? "selected" : ""}`} key={candidate.id || index}>
                  <label className="aiImportSelect">
                    <input
                      type="checkbox"
                      checked={Boolean(candidate.selected)}
                      onChange={(event) => updateImportCandidate(index, { selected: event.target.checked })}
                    />
                    <span>{t("create_candidate")}</span>
                  </label>
                  <div className="aiImportFields">
                    <label>{t("firstName")}<input value={candidate.firstName || ""} onChange={(event) => updateImportCandidate(index, { firstName: event.target.value })} /></label>
                    <label>{t("lastName")}<input value={candidate.lastName || ""} onChange={(event) => updateImportCandidate(index, { lastName: event.target.value })} /></label>
                    <label>{t("email")}<input value={candidate.email || ""} onChange={(event) => updateImportCandidate(index, { email: event.target.value })} /></label>
                    <label>{t("phone")}<input value={candidate.phone || ""} onChange={(event) => updateImportCandidate(index, { phone: event.target.value })} /></label>
                    <label>{t("type")}<select value={candidate.type || "other"} onChange={(event) => updateImportCandidate(index, { type: event.target.value })}>{typeOptions.map((type) => <option value={type} key={type}>{t(`types.${type}`)}</option>)}</select></label>
                    <label>{t("status_label")}<select value={candidate.status || "active"} onChange={(event) => updateImportCandidate(index, { status: event.target.value })}>{statusOptions.map((status) => <option value={status} key={status}>{t(`status.${status}`)}</option>)}</select></label>
                    <label>{t("access_label")}<input value={candidate.accessLabel || ""} onChange={(event) => updateImportCandidate(index, { accessLabel: event.target.value })} /></label>
                    <label>{t("zone")}<input value={candidate.zone || ""} onChange={(event) => updateImportCandidate(index, { zone: event.target.value })} /></label>
                    <label>{t("event_scope")}<select value={candidate.eventId || ""} onChange={(event) => updateImportCandidate(index, { eventId: event.target.value })}>
                      <option value="">{t("all_events")}</option>
                      {events.map((event) => (
                        <option value={event.id} key={event.id}>{event.name || event.title || event.id}</option>
                      ))}
                    </select></label>
                    <label>{t("valid_from")}<input value={candidate.validFrom || ""} onChange={(event) => updateImportCandidate(index, { validFrom: event.target.value })} /></label>
                    <label>{t("valid_until")}<input value={candidate.validUntil || ""} onChange={(event) => updateImportCandidate(index, { validUntil: event.target.value })} /></label>
                    <label>{t("max_scans_label")}<input value={candidate.maxScans || ""} onChange={(event) => updateImportCandidate(index, { maxScans: event.target.value })} /></label>
                    <label>{t("notes")}<input value={candidate.notes || ""} onChange={(event) => updateImportCandidate(index, { notes: event.target.value })} /></label>
                  </div>
                  <div className="aiImportMeta">
                    <span>{Math.round(Number(candidate.confidence || 0) * 100)}%</span>
                    {!!candidate.warnings?.length && <small>{candidate.warnings.join(", ")}</small>}
                    <button type="button" onClick={() => removeImportCandidate(index)}>{t("remove")}</button>
                  </div>
                </article>
              ))}
            </div>
            <footer>
              <button type="button" className="secondaryAction" onClick={() => setImportPreview(null)}>{t("cancel")}</button>
              <button
                type="button"
                className="primaryAction"
                onClick={confirmImport}
                disabled={saving || !importPreview.candidates.some((candidate) => candidate.selected)}
              >
                {saving ? t("saving") : t("confirm_ai_import")}
              </button>
            </footer>
          </div>
        </div>
      )}

      <EmailCustomizationModal
        event={{
          name: getOrganizationDisplayName(org),
          logoUrl: org?.logoUrl || org?.imageUrl || org?.avatar || "",
          ticketEmailCustomization: accreditationEmailCustomization,
        }}
        isOpen={showEmailCustomizationModal}
        onClose={() => setShowEmailCustomizationModal(false)}
        onSave={saveAccreditationEmailCustomization}
      />
    </div>
  );
}

export default Accreditations;
